import { acceptHMRUpdate, defineStore, storeToRefs } from "pinia";
import { computed, ref } from "vue";
import { DateTime } from "luxon";

import LocalForagePlugin, { syncState } from "@/plugins/LocalForagePlugin";
import { sectorHistory } from "@/plugins/LocalForagePlugin";

import { useApplicationStore } from "@/stores/Application";
import { useAuthStore } from "@/stores/Auth";
import { hydrate, refreshFromServer } from "@/services/data/sync/sector-history-sync-coordinator";
import { Unauthorized } from "@/types/api/responses";

import type { Tuple } from "@/types/adts";
import type { CompositionContext } from "@/services/data/sync-supervisor";
import type { PersistedSectorsState, SectorAlert, SectorMarketState } from "@/types/entities";
import { formatDateAsSortableString, fromSortableString, normalizeDateTime } from "@/helpers/dateTimeUtilities";

// Non-reactive variables
import { consoleLoggerFactory, getDefaultLoggerOptions, ILogger, LogLevels } from "@/types/console-logger";
const logger: ILogger = consoleLoggerFactory.create( getDefaultLoggerOptions( LogLevels.ERROR ) );

import { clientStateManager } from "@/services/error-services";
import { useLocalStorage } from "@vueuse/core";

export type SectorMarketStateContainer = {
    date: string,
    history: SectorMarketState[]
}

const error = ref< string | undefined >();
const initialized = ref< boolean >( false );
const fetching = ref< boolean >( false );
const loading = ref< boolean >( false );

const isFetching = computed< boolean >( () => fetching.value );
const isLoading = computed< boolean >( () => loading.value );

const sectorHistoryRepository =
    ref< Map< string, Map< number, SectorMarketState > > >( new Map< string, Map< number, SectorMarketState > >() );

const allSectorAlertsUniquenessTracker = new Set< string >();
const allSectorAlerts = ref< SectorAlert[] >( [] );

const selectedDate = ref< string >( '' );

const dateOfMostRecentMarketDataAvailable = useLocalStorage< string >( 'latest-dt', '' );


export const useSectorHistoryStore = defineStore( "sector-history", () => {
    // Depends on authStore for authentication state and appStore to signal that data is being loaded
    const appStore = useApplicationStore();
    const { authToken } = storeToRefs( useAuthStore() );

    // const { dateOfMostRecentMarketDataAvailable } = storeToRefs( appStore );

    const latestMarketDataRetrieved = computed< string | undefined >( () => {
        const dates = [ ...( Array.from( sectorHistoryRepository.value.keys() ) ) ]
            .sort( ( d1: string, d2: string ) => d2.localeCompare( d1 ) );

        return dates.length > 0 ? dates[ 0 ] : undefined;
    } );

    // Clear all internal tracking and state variables
    function reset() {
        error.value = undefined;
        initialized.value = false;
        loading.value = false;

        resetSectorHistory();
    }

    async function initializeAsync() {
        if ( initialized.value || loading.value ) {
            logger.debug( '[ Sector-History ].initialize() exiting - already processing...' );
            return;
        }

        loading.value = true;

        // Initialize the "current" selected date based on what is available within the Database
        selectedDate.value = dateOfMostRecentMarketDataAvailable.value;

        // Wait for the driver to settle
        if ( typeof sectorHistory === 'undefined' || typeof syncState === 'undefined' ) {
            LocalForagePlugin.initialize();
        }

        await sectorHistory.ready();

        // Query IDB for the number of sector history records that already exist in the database
        const len = await sectorHistory.length();
        if ( len > 0 ) {
            // Clear the contents of the collections
            sectorHistoryRepository.value = new Map< string, Map< number, SectorMarketState > >();

            // Iterate over all elements within IDB
            const loadedStateData: PersistedSectorsState[] = [];
            sectorHistory.iterate( ( history: PersistedSectorsState ) => {
                loadedStateData.push( history );
                return;
            } ).then( () => {
                buildReactiveReferences( loadedStateData );
                initialized.value = true;
            } ).finally( () => loading.value = false );
        } else {
            // Initial "starting point"
            const dateOfLatestMarketDataReceived: string | undefined = latestMarketDataRetrieved.value;
            const latestRetrievedUtcDate: DateTime = dateOfLatestMarketDataReceived ?
                // If there was any data in the IDB repository, look for the most recent date and use this (-3) as the
                // initial date to be able to easily refresh the most recent data in case of an update
                normalizeDateTime( dateOfLatestMarketDataReceived )!.plus( { days: -3 } ) :
                // Otherwise, pull back the previous 6 months worth of data (as a starting point)
                fromSortableString(appStore.dateOfMostRecentMarketDataAvailable);

            if ( authToken.value ) {
                const missingDates = [ formatDateAsSortableString( latestRetrievedUtcDate ) ];
                refreshFromServer( authToken.value, appStore.compositionCtx, missingDates )
                    .then( ( sectorStateData: PersistedSectorsState[] ) => {
                        buildReactiveReferences( sectorStateData );
                        initialized.value = true;
                    } )
                    .catch( async ( err: any ) => {
                        // If anything went wrong while attempting to initialize the store, reset it and then handle the specific error
                        reset();

                        clientStateManager.queueClientError( err, {
                            token: authToken.value,
                            missingDates: missingDates
                        } );
                    } )
                    .finally( () => loading.value = false );
            }
        }
    }

    function buildReactiveReferences( sectorStateData: PersistedSectorsState[] ): void {
        let alerts: SectorAlert[] = [];
        sectorStateData.forEach( ( state: PersistedSectorsState ) => {
            state.sectors.forEach( ( sectorState: SectorMarketState ) => {
                if ( sectorState.alerts && sectorState.alerts.length > 0 ) {
                    alerts = [ ...sectorState.alerts, ...alerts ];
                }
            });
        } );

        alerts.sort( ( sa1: SectorAlert, sa2: SectorAlert ) => sa2.dt.localeCompare( sa1.dt ) );
        allSectorAlerts.value = [ ...alerts ];
    }

    const sectorStateForSelectedDate = computed< SectorMarketStateContainer >( () => {
        let sectorData: SectorMarketState[] = [];

        const currentlySelectedDate = selectedDate.value;
        if ( !!currentlySelectedDate && sectorHistoryRepository.value.has( currentlySelectedDate ) ) {
            const repositoryDateForSelectedDate: Map< number, SectorMarketState > | undefined = sectorHistoryRepository.value.get( currentlySelectedDate );
            if ( repositoryDateForSelectedDate ) {
                sectorData = [ ...( Array.from( repositoryDateForSelectedDate.values() ) ) ];
            }
        }

        return {
            date: selectedDate.value,
            history: sectorData
        };
    } );

    async function setSelectedDate( selectedDateAsSortableString: string ): Promise< void > {
        await initializeAsync();

        logger.debug( `selectedDateAsSortableString: ${ selectedDateAsSortableString }, alreadyAvailable: ${ sectorHistoryRepository.value.has( selectedDateAsSortableString ) }` );

        if ( authToken.value ) {
            if ( !sectorHistoryRepository.value.has( selectedDateAsSortableString ) ) {
                try {
                    fetching.value = true;
                    logger.debug( `Date ${ selectedDateAsSortableString } is not currently in memory, fetching...` );

                    const sectorHistory: PersistedSectorsState[] =
                        await refreshFromServer( authToken.value, appStore.compositionCtx, [ selectedDateAsSortableString ] );

                    if ( sectorHistory?.length > 0 ) {
                        for ( const marketData of sectorHistory ) {
                            hydrateFromContainer( marketData, appStore.compositionCtx );
                        }
                    }
                } catch ( err: any ) {
                    // If anything went wrong while attempting to initialize the store, reset it and then handle the specific error
                    reset();

                    clientStateManager.queueClientError( err, {
                        token: authToken.value,
                        selectedDate: selectedDateAsSortableString
                    } );
                } finally {
                    fetching.value = false;
                }
            }
        } else {
            throw new Unauthorized();
        }

        selectedDate.value = selectedDateAsSortableString;
    }

    const mostRecentSectorData = computed< SectorMarketState[] >( () => {
        let latestSectorHistory: SectorMarketState[] = [];

        if ( sectorHistoryRepository.value.size > 0 ) {
            const datesInMemory = [ ...sectorHistoryRepository.value.keys() ]
                .sort( ( d1: string, d2: string ): number => d2.localeCompare( d1 ) );

            const mostRecentDate: string = datesInMemory[ 0 ];
            latestSectorHistory = [ ...( Array.from( sectorHistoryRepository.value.get( mostRecentDate )!.values() ) ) ];
        }

        return latestSectorHistory;
    } );

    function resetSectorHistory(): void {
        sectorHistoryRepository.value?.clear();
    }

    function hydrateFromContainer( entity: PersistedSectorsState, ctx: CompositionContext ): void {
        if ( !( allSectorAlerts.value ) ) {
            allSectorAlerts.value = [];
            allSectorAlertsUniquenessTracker.clear();
        }

        if ( !( sectorHistoryRepository.value ) ) {
            sectorHistoryRepository.value = new Map< string, Map< number, SectorMarketState > >();
        }

        const result: Tuple< string, Map< number, SectorMarketState > > = hydrate( entity, ctx );
        sectorHistoryRepository.value.set( result.item1, result.item2 );
    }

    return {
        // ACTIONS
        initializeAsync,
        reset,
        setSelectedDate,

        // GETTERS
        allSectorAlerts,
        error,
        isFetching,
        isLoading,
        mostRecentSectorData,
        sectorStateForSelectedDate
    }
} );

if ( import.meta.hot ) {
    import.meta.hot.accept( acceptHMRUpdate( useSectorHistoryStore, import.meta.hot ) );
}
